/**
 * Admin → Conflicts page.
 *
 * Edits `settings/conflictsPage`: every piece of copy on the standalone /conflicts
 * request page, plus whether the matter-description field is shown and required.
 *
 * Shape and defaults live in lib/conflictsPageSettings.js so this editor and the page it
 * configures can never disagree; a blank field here means "use the shipped default"
 * rather than an empty heading on the live page.
 */

import { useEffect, useState } from 'react';
import SubFieldRowsEditor from '../../components/SubFieldRowsEditor';
import { sanitizeSubFieldRows } from '../../lib/fieldLayout';
import { CallStatus, useCallable } from '../../lib/useCallable';
import { useDoc } from '../../lib/useFirestore';
import {
  DEFAULT_CONFLICTS_PAGE,
  NAME_PARTY_FIELD,
  normalizeConflictsPage,
} from '../../lib/conflictsPageSettings';

/** Grouped for the editor only — the stored document stays flat. */
const GROUPS = [
  {
    title: 'Header',
    fields: [
      ['eyebrow', 'Eyebrow (small text above the title)', 'input'],
      ['title', 'Page title', 'input'],
      ['intro', 'Introduction', 'textarea'],
    ],
  },
  {
    title: 'Requesting party',
    fields: [
      ['requestingPartyTitle', 'Section heading', 'input'],
      ['requestingPartyHelp', 'Help text', 'textarea'],
    ],
  },
  {
    title: 'Email subject line',
    fields: [
      ['subjectLabel', 'Field label', 'input'],
      ['subjectHelp', 'Help text', 'textarea'],
    ],
  },
  {
    title: 'Names and entities',
    fields: [
      ['partiesTitle', 'Section heading', 'input'],
      ['partiesHelp', 'Help text', 'textarea'],
      ['partiesValidationMessage', 'Message when no name is entered', 'input'],
    ],
  },
  {
    title: 'Matter description',
    fields: [
      ['descriptionLabel', 'Field label', 'input'],
      ['descriptionHelp', 'Help text', 'textarea'],
      ['descriptionValidationMessage', 'Message when required and left blank', 'input'],
    ],
  },
  {
    title: 'Submission',
    fields: [
      ['submitLabel', 'Submit button', 'input'],
      ['submittedTitle', 'Confirmation heading', 'input'],
      ['submittedBody', 'Confirmation message', 'textarea'],
      ['submitAnotherLabel', 'Submit-another button', 'input'],
    ],
  },
];

export default function AdminConflictsPage() {
  const { data: stored, loading } = useDoc('settings/conflictsPage');
  const { run, busy, error, saved } = useCallable();
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (!loading && !draft) setDraft(normalizeConflictsPage(stored));
  }, [stored, loading, draft]);

  if (loading || !draft) {
    return <p className="text-sm text-slate-500 dark:text-slate-400">Loading conflicts page settings…</p>;
  }

  const set = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));
  const save = () =>
    run('adminUpdateSettings', {
      docId: 'conflictsPage',
      value: {
        ...draft,
        partyFields: sanitizeSubFieldRows(draft.partyFields).filter((f) => f.path !== 'name'),
      },
    });
  const resetAll = () => setDraft({ ...DEFAULT_CONFLICTS_PAGE });

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-white p-4 shadow dark:bg-slate-900">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              Conflicts request page
            </h2>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Wording and options for the standalone conflict-check request page at{' '}
              <code>/conflicts</code>. Clearing a box restores its default wording.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <CallStatus busy={busy} error={error} saved={saved} />
            <button
              type="button"
              onClick={resetAll}
              className="rounded border border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Restore all defaults
            </button>
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="rounded bg-firm-700 px-4 py-2 text-sm font-medium text-white hover:bg-firm-900 disabled:opacity-50 dark:bg-firm-500 dark:hover:bg-firm-400"
            >
              Save conflicts page
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-lg bg-white p-4 shadow dark:bg-slate-900">
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Matter description field</h3>
        <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-700 dark:text-slate-200">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={draft.descriptionEnabled}
              onChange={(event) => set('descriptionEnabled', event.target.checked)}
            />
            Show the matter description field
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={draft.descriptionRequired}
              disabled={!draft.descriptionEnabled}
              onChange={(event) => set('descriptionRequired', event.target.checked)}
            />
            Require it before submitting
          </label>
        </div>
        {!draft.descriptionEnabled && (
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            Hidden: requesters will submit names and entities only.
          </p>
        )}
      </div>

      <div className="rounded-lg bg-white p-4 shadow dark:bg-slate-900">
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Party list columns</h3>
        <p className="mt-1 max-w-2xl text-xs text-slate-500 dark:text-slate-400">
          Every entry always has a <strong>{NAME_PARTY_FIELD.label}</strong> field — that's
          the shipped default, a plain list of names. Add columns below only if Conflicts
          needs more per entry (entity type, role, notes, ...).
        </p>
        <div className="mt-2">
          <SubFieldRowsEditor
            fields={draft.partyFields}
            onChange={(next) => set('partyFields', next)}
            legend="Extra columns (beyond Name)"
          />
        </div>
      </div>

      {/*
        Said once, above the lot, rather than repeated under every box: the formatting is the same
        everywhere on this page, and a hint on nine fields is nine things to read.
      */}
      <p className="rounded-lg bg-white px-4 py-3 text-xs text-slate-600 shadow dark:bg-slate-900 dark:text-slate-300">
        In any of the longer boxes you can put a word in <strong>bold</strong> by wrapping it in two
        asterisks — <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">**IMPORTANT**</code>{' '}
        — and start a new line by pressing Enter. Nothing else is interpreted, so an asterisk on its
        own shows up as an asterisk.
      </p>

      {GROUPS.map((group) => (
        <div key={group.title} className="rounded-lg bg-white p-4 shadow dark:bg-slate-900">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">{group.title}</h3>
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            {group.fields.map(([key, label, kind]) => (
              <label key={key} className="text-xs text-slate-600 dark:text-slate-300">
                {label}
                {kind === 'textarea' ? (
                  <textarea
                    className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-900"
                    rows={3}
                    value={draft[key]}
                    placeholder={DEFAULT_CONFLICTS_PAGE[key]}
                    onChange={(event) => set(key, event.target.value)}
                  />
                ) : (
                  <input
                    className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-900"
                    value={draft[key]}
                    placeholder={DEFAULT_CONFLICTS_PAGE[key]}
                    onChange={(event) => set(key, event.target.value)}
                  />
                )}
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
